import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Building2, ChevronRight, Shield, FileCheck, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TitleAuthPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    company_name: "",
    contact_name: "",
    phone: "",
    license_number: "",
    address: "",
    service_areas: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/api/title/auth/login" : "/api/title/auth/register";
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : {
            ...formData,
            service_areas: formData.service_areas.split(",").map(s => s.trim()).filter(Boolean)
          };

      await axios.post(`${BACKEND_URL}${endpoint}`, payload, { withCredentials: true });
      toast.success(isLogin ? "Welcome back!" : "Registration successful!");
      navigate("/title/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-black via-[#0A0A0A] to-black p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzI3MjcyYSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20" />
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-black text-white tracking-tight">DEALFORGE</h1>
              <p className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] text-sm font-medium">TITLE PORTAL</p>
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <h2 className="font-heading text-4xl font-bold text-white mb-4 leading-tight">
              Streamline Your<br />
              <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">Title Operations</span>
            </h2>
            <p className="text-white/50 text-lg max-w-md">
              Complete workflow management for title companies. Track orders, manage searches, coordinate closings, and grow your business.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08]">
              <FileCheck className="w-8 h-8 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
              <div>
                <p className="text-white font-medium">Full Order Tracking</p>
                <p className="text-white/50 text-sm">9-stage workflow from order to policy</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08]">
              <Clock className="w-8 h-8 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
              <div>
                <p className="text-white font-medium">Automated Workflows</p>
                <p className="text-white/50 text-sm">Stage updates, reminders, and alerts</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08]">
              <Building2 className="w-8 h-8 text-gold" />
              <div>
                <p className="text-white font-medium">Investor Network</p>
                <p className="text-white/50 text-sm">Direct connection to deal pipeline</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-white/40 text-sm">
          Part of the DealForge Ecosystem
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
              </div>
              <div className="text-left">
                <h1 className="font-heading text-xl font-black text-white">DEALFORGE</h1>
                <p className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] text-xs font-medium">TITLE PORTAL</p>
              </div>
            </div>
          </div>

          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-8">
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-bold text-white uppercase">
                {isLogin ? "Sign In" : "Register"}
              </h2>
              <p className="text-white/50 mt-1">
                {isLogin ? "Access your title company dashboard" : "Create your title company account"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company_name" className="text-white/70">Company Name *</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        required={!isLogin}
                        className="bg-black border-white/[0.08] text-white mt-1"
                        placeholder="ABC Title Co."
                        data-testid="title-company-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_name" className="text-white/70">Contact Name *</Label>
                      <Input
                        id="contact_name"
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                        required={!isLogin}
                        className="bg-black border-white/[0.08] text-white mt-1"
                        placeholder="John Smith"
                        data-testid="title-contact-name-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-white/70">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-black border-white/[0.08] text-white mt-1"
                        placeholder="(555) 123-4567"
                        data-testid="title-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="license_number" className="text-white/70">License #</Label>
                      <Input
                        id="license_number"
                        name="license_number"
                        value={formData.license_number}
                        onChange={handleChange}
                        className="bg-black border-white/[0.08] text-white mt-1"
                        placeholder="TL-12345"
                        data-testid="title-license-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="service_areas" className="text-white/70">Service Areas</Label>
                    <Input
                      id="service_areas"
                      name="service_areas"
                      value={formData.service_areas}
                      onChange={handleChange}
                      className="bg-black border-white/[0.08] text-white mt-1"
                      placeholder="Dallas, Fort Worth, Houston (comma separated)"
                      data-testid="title-service-areas-input"
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="email" className="text-white/70">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="you@titlecompany.com"
                  data-testid="title-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-white/70">Password *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="bg-black border-white/[0.08] text-white mt-1"
                  placeholder="Min 6 characters"
                  data-testid="title-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-none"
                data-testid="title-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isLogin ? "Signing In..." : "Creating Account..."}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {isLogin ? "Sign In" : "Create Account"}
                    <ChevronRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] hover:text-emerald-300 text-sm"
                data-testid="title-toggle-auth-btn"
              >
                {isLogin ? "Need an account? Register" : "Already have an account? Sign In"}
              </button>
            </div>
          </div>

          <p className="text-center text-white/40 text-sm mt-6">
            By continuing, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
}
